import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  FileText, 
  Plus, 
  Wrench, 
  Trash2, 
  Shield, 
  AlertTriangle,
  Tag,
  Calendar,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface ChangelogEntry {
  version: string;
  date: string;
  sections: {
    type: 'added' | 'changed' | 'deprecated' | 'removed' | 'fixed' | 'security';
    items: string[];
  }[];
}

const SECTION_CONFIG = {
  added: { label: 'Agregado', icon: Plus, color: 'bg-green-500/10 text-green-500 border-green-500/20' },
  changed: { label: 'Cambiado', icon: Wrench, color: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  deprecated: { label: 'Deprecado', icon: AlertTriangle, color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' },
  removed: { label: 'Eliminado', icon: Trash2, color: 'bg-red-500/10 text-red-500 border-red-500/20' },
  fixed: { label: 'Corregido', icon: Wrench, color: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
  security: { label: 'Seguridad', icon: Shield, color: 'bg-purple-500/10 text-purple-500 border-purple-500/20' },
};

// Parsed changelog data - in production this could be fetched from an API
const CHANGELOG_DATA: ChangelogEntry[] = [
  {
    version: '1.6.1',
    date: '2026-05-02',
    sections: [
      {
        type: 'fixed',
        items: [
          'Reproductor de cámara: "illegal codec" en streams HEVC del NVR — ahora se fuerza transcodificación H.264 vía go2rtc (&video=h264&mp4) en HLS',
          'ExoPlayer crash IllegalArgumentException por bufferConfig inválido (CameraPlayerScreen y CamerasScreen)',
          'LiveThumb: cuando había 2+ cámaras en un sitio sólo reproducía la última (cold-start race en go2rtc). Solución preservando on-demand: el cliente llama POST /api/go2rtc/warm al abrir el sitio (gateway adjunta preload temporal por stream) y POST /api/go2rtc/cool al cerrarlo, dejando los pipelines RTSP apagados cuando nadie mira',
          'Supabase advisor "Security Definer View" sobre site_cameras: vista recreada con security_invoker = true',
        ],
      },
      {
        type: 'changed',
        items: [
          'apps/mobile-client services/cameras.ts: removido buildGatewayMp4Url, todos los URLs pasan por HLS H.264 forzado',
          'gateway: nuevo endpoint POST /api/go2rtc/preload-all para back-fill de preload en streams ya registrados',
          'Nueva migración 20260502010000_security_advisors_fixes.sql',
        ],
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-05-01',
    sections: [
      {
        type: 'added',
        items: [
          'Móvil: chat de tickets con multimedia real (imagen, video con controles, audio play/pause)',
          'Móvil: grabación de audio en el chat (hold-to-stop) con permisos RECORD_AUDIO',
          'Móvil Home: tarjetas de Alertas activas (gestionables → AlertTracking) y Alarmas 24h con refresh 60s',
          'Web operador: TicketChatPanel con realtime y firmado de URLs',
          'Web emergencia: mini-mapa Uber-style ahora muestra #ticket_number y nombre de cliente',
          'Configuración del SOS por agitación (sensibilidad y cuenta regresiva)',
          'SOS por agitación disponible también en mobile-patrol',
          'AccountDetailPage: pestaña de destinatarios de alerta por cuenta',
          'Vista de tickets de miembros visible para account owner',
        ],
      },
      {
        type: 'changed',
        items: [
          'Bucket evidence pasa a privado: rutas se firman bajo demanda con createSignedUrl(60m)',
          'Botón móvil "Historial" renombrado a "Alarmas"',
          'AlertDetail/TicketChatPanel con tema dual light/dark completo',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Web chat: composer con texto invisible (text-white sobre fondo claro)',
          'Web chat: auto-scroll al último mensaje aunque la multimedia tarde en cargar',
          '404 al abrir evidencia (getPublicUrl en bucket privado)',
          'ArcEventsPage: type error en batchSiteConfig (siteId opcional)',
        ],
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-04-30',
    sections: [
      {
        type: 'added',
        items: [
          'Tracking de ubicación de clientes (background) en mobile-client',
          'Iconos de marcador personalizables (initials, foto de perfil) compartidos',
          'Página de Auditoría con filtros y export',
          'Sounds personalizados para alertas en web',
        ],
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-04-28',
    sections: [
      {
        type: 'added',
        items: [
          'Mapa de emergencia rediseñado tipo Uber (mini-mapa para alerta activa)',
          'ClientList y PatrolList laterales en EmergencyPage',
          'Edge function process-alert para ruteo y push',
        ],
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-04-25',
    sections: [
      {
        type: 'added',
        items: [
          'Sistema RBAC completo con permisos granulares',
          'Página de Roles con asignación por usuario',
          'Página de Usuarios con creación vía Edge Function create-user',
        ],
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-04-16',
    sections: [
      {
        type: 'added',
        items: [
          'Módulo de Emergencia con cola de alertas y mapa global',
          'Notificaciones push FCM end-to-end',
        ],
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-04-16',
    sections: [
      {
        type: 'added',
        items: [
          'Sistema de provisioning de sitios (site-onboard.ps1)',
          'WireGuard VPN para gateways en sitios remotos',
          'Topología de red documentada',
        ],
      },
    ],
  },
  {
    version: '0.9.0',
    date: '2026-04-01',
    sections: [
      {
        type: 'added',
        items: [
          'go2rtc como engine alternativo de captura',
          'Cámaras HCT (Hik-Connect) streaming via gateway',
        ],
      },
    ],
  },
  {
    version: '0.8.0',
    date: '2026-03-29',
    sections: [
      {
        type: 'added',
        items: [
          'Soporte completo Hikvision HPP para paneles de alarma',
          'Sincronización masiva de paneles desde Partner Pro',
        ],
      },
    ],
  },
  {
    version: '0.7.0',
    date: '2026-03-29',
    sections: [
      {
        type: 'added',
        items: [
          'Página ARC Events con dashboard de eventos en tiempo real',
          'Procesamiento por lote de eventos (batch processing)',
          'Códigos CID Ademco precargados (~200)',
        ],
      },
    ],
  },
  {
    version: '0.6.0',
    date: '2026-03-28',
    sections: [
      {
        type: 'added',
        items: [
          'Módulo ARC completo para Central Receptora de Alarmas',
          'Página de Centro ARC (/arc/events) con dashboard de eventos en tiempo real',
          'Página de Contactos ARC (/arc/contacts) con CRUD completo por sitio',
          'Página de Códigos CID (/arc/cid-codes) con ~200 códigos Ademco estándar',
          'Polling MQ para recepción de eventos HPP en tiempo real',
          'Store Zustand arc-store.ts para gestión de estado ARC',
          'Servicio arc.ts con operaciones CRUD, polling y suscripción realtime',
          'Vinculación de cámaras a sitios de alarma',
          'Campo site_id en tabla cameras para verificación de video',
          'Vista site_cameras con información de sitio',
          'Funciones linkCameraToSite, getCamerasBySite, getCamerasWithSites',
          'Componentes UI Radix: Dialog, Switch, Label',
          'Migraciones: seed_cid_codes.sql, cameras_site_link.sql',
        ],
      },
      {
        type: 'fixed',
        items: ['Logging mejorado en sincronización de paneles HPP'],
      },
    ],
  },
  {
    version: '0.5.0',
    date: '2026-03-28',
    sections: [
      {
        type: 'added',
        items: [
          'Esquema ARC extendido (migración 20260328000003)',
          'Tabla arc_contacts — Contactos de emergencia por sitio',
          'Tabla arc_events — Eventos CID completos',
          'Tabla arc_call_log — Registro de llamadas de verificación',
          'Tabla cid_codes — Catálogo de códigos Contact ID',
          'Tipos TypeScript en @asi/shared: ArcContact, ArcEvent, ArcCallLog, CidCode',
          'Documentación de arquitectura del sistema',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Credenciales HCT no cargaban en Gateway (ESM hoisting)',
          'Tabla alarm_panels no existía (migración no ejecutada)',
          'RLS bloqueaba INSERT en tablas de alarmas',
        ],
      },
    ],
  },
  {
    version: '0.4.0',
    date: '2026-03-27',
    sections: [
      {
        type: 'added',
        items: [
          'Integración Hik-Connect (HCT) con OAuth2',
          'Integración Hik Partner Pro (HPP) para paneles de alarma',
          'Página de Alarmas (/alarms) con lista de paneles y eventos',
          'Suscripción a Message Queue para eventos en tiempo real',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Manejo de errores en refresh de tokens HCT',
          'Políticas RLS para recursión infinita',
        ],
      },
    ],
  },
  {
    version: '0.3.0',
    date: '2026-03-26',
    sections: [
      {
        type: 'added',
        items: [
          'Gateway ONVIF para streaming de cámaras',
          'Conversión RTSP a HLS via FFmpeg',
          'Gestión de cámaras en web con CRUD completo',
          'Reproductor HLS integrado',
          'Grid de vista múltiple de cámaras',
        ],
      },
    ],
  },
  {
    version: '0.2.0',
    date: '2026-03-25',
    sections: [
      {
        type: 'added',
        items: [
          'Dashboard principal con métricas en tiempo real',
          'Gestión de tickets/alertas con workflow de estados',
          'Mapa de patrulleros con tracking GPS',
          'Geocercas con editor visual',
        ],
      },
      {
        type: 'security',
        items: [
          'Row Level Security (RLS) en todas las tablas',
          'Autenticación via Supabase Auth',
          'Roles y permisos por organización',
        ],
      },
    ],
  },
  {
    version: '0.1.0',
    date: '2026-03-20',
    sections: [
      {
        type: 'added',
        items: [
          'Estructura inicial del monorepo (pnpm workspaces)',
          'Aplicación web React + Vite + TailwindCSS',
          'Aplicación móvil cliente (React Native CLI)',
          'Aplicación móvil patrullero (React Native CLI)',
          'Paquete compartido @asi/shared para tipos y utilidades',
          'Configuración de Supabase (PostgreSQL + PostGIS)',
          'Docker Compose para Jitsi Meet + Gateway + Nginx',
        ],
      },
    ],
  },
];

function VersionBadge({ version }: { version: string }) {
  const isUnreleased = version === 'Unreleased';
  return (
    <Badge 
      variant={isUnreleased ? 'outline' : 'default'}
      className={isUnreleased ? 'border-dashed' : ''}
    >
      <Tag className="w-3 h-3 mr-1" />
      v{version}
    </Badge>
  );
}

function SectionBadge({ type }: { type: keyof typeof SECTION_CONFIG }) {
  const config = SECTION_CONFIG[type];
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={config.color}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
}

function ChangelogEntryCard({ entry, isExpanded, onToggle }: { 
  entry: ChangelogEntry; 
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <Card className="mb-4">
      <CardHeader 
        className="pb-2 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <VersionBadge version={entry.version} />
            {entry.date && (
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {entry.date}
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent>
          {entry.sections.map((section, idx) => (
            <div key={idx} className="mb-4 last:mb-0">
              <div className="mb-2">
                <SectionBadge type={section.type} />
              </div>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground pl-2">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

export function ChangelogPage() {
  const currentVersion = CHANGELOG_DATA.find(e => e.version !== 'Unreleased')?.version || '0.0.0';
  
  // Pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set([CHANGELOG_DATA[0]?.version]));
  const itemsPerPage = 5;

  // Filter by date range
  const filteredData = CHANGELOG_DATA.filter(entry => {
    if (!startDate && !endDate) return true;
    if (!entry.date) return false;
    
    const entryDate = new Date(entry.date);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    
    if (start && entryDate < start) return false;
    if (end && entryDate > end) return false;
    return true;
  });

  // Paginate
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const paginatedData = filteredData.slice(startIdx, endIdx);

  // Toggle expanded state
  const toggleVersion = (version: string) => {
    setExpandedVersions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(version)) {
        newSet.delete(version);
      } else {
        newSet.add(version);
      }
      return newSet;
    });
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Historial de Cambios
          </h1>
          <p className="text-muted-foreground mt-1">
            Registro de todas las versiones y cambios del sistema
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-muted-foreground">Versión actual</div>
          <div className="text-2xl font-mono font-bold">{currentVersion}</div>
        </div>
      </div>

      <Card className="mb-6 bg-muted/50">
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-500/10 text-green-500 border-green-500/20" variant="outline">
                <Plus className="w-3 h-3 mr-1" /> Agregado
              </Badge>
              <span className="text-muted-foreground">Nueva funcionalidad</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20" variant="outline">
                <Wrench className="w-3 h-3 mr-1" /> Corregido
              </Badge>
              <span className="text-muted-foreground">Bugs resueltos</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-purple-500/10 text-purple-500 border-purple-500/20" variant="outline">
                <Shield className="w-3 h-3 mr-1" /> Seguridad
              </Badge>
              <span className="text-muted-foreground">Mejoras de seguridad</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Date filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 block">Desde</label>
              <Input 
                type="date" 
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 block">Hasta</label>
              <Input 
                type="date" 
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full"
              />
            </div>
            {(startDate || endDate) && (
              <Button 
                variant="outline" 
                onClick={() => { setStartDate(''); setEndDate(''); }}
                size="sm"
              >
                Limpiar
              </Button>
            )}
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            Mostrando {filteredData.length} {filteredData.length === 1 ? 'versión' : 'versiones'}
          </div>
        </CardContent>
      </Card>

      <Separator className="my-6" />

      <div className="space-y-4 min-h-[400px]">
        {paginatedData.length > 0 ? (
          paginatedData.map((entry, idx) => (
            <ChangelogEntryCard 
              key={idx} 
              entry={entry} 
              isExpanded={expandedVersions.has(entry.version)}
              onToggle={() => toggleVersion(entry.version)}
            />
          ))
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No se encontraron versiones en el rango de fechas seleccionado.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Siguiente
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      <Separator className="my-6" />

      <div className="text-center text-sm text-muted-foreground">
        <p>
          Formato basado en{' '}
          <a 
            href="https://keepachangelog.com/es-ES/1.1.0/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            Keep a Changelog <ExternalLink className="w-3 h-3" />
          </a>
        </p>
        <p className="mt-1">
          Versionado según{' '}
          <a 
            href="https://semver.org/lang/es/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            Semantic Versioning <ExternalLink className="w-3 h-3" />
          </a>
        </p>
      </div>
    </div>
  );
}
